// ClientTableSort — client-side table sorting without a server round-trip (ts-6).
//
// Replaces six server-side sort handlers (sort_repos, sort_runners,
// sort_env_hosts, sort_members, sort_prs) that do nothing but toggle a sort
// key/direction and re-render from an in-memory assigns list.
//
// Markup convention:
//   Attach phx-hook="ClientTableSort" to the <table> element.
//   Sort header buttons: data-sort-col="<key>" on <th> or a child button.
//   Sortable cells:      data-col="<key>" on <td>.
//   Current sort state:  data-sort-key and data-sort-dir on the <table>.
//
// Sort is purely ephemeral — page reload resets to server-rendered order.
// Numeric columns are detected only when every non-empty cell value is numeric;
// everything else is compared as a locale string.

import type { LiveViewHook } from "./types";
import { validSortDir, type SortDir } from "../../../.dox/ui";

export interface ClientTableSortState {
  _onClick: (e: Event) => void;
}

// Pure: read the sort value off a <td> cell. Prefers an explicit
// `data-sort-value` (server-stamped canonical form) and falls back to the
// cell's text content. Exported for property testing.
export const getCellValue = (row: HTMLTableRowElement, key: string): string => {
  const cell = row.querySelector<HTMLElement>(
    `[data-col="${CSS.escape(key)}"]`,
  );
  if (!cell) return "";
  // `dataset["sortValue"]` is `string | undefined` under
  // `noUncheckedIndexedAccess`, narrowed to `string` when present.
  // `textContent` is typed `string` in this project's lib config
  // (ESLint flags `?.trim()` here as a redundant optional chain), so
  // bare `.trim()` is type-safe.
  return cell.dataset["sortValue"] ?? cell.textContent.trim();
};

// Pure: detect whether the column is numeric. A mixed column must stay in the
// string branch so a second pass does not reclassify the already-sorted values.
export const isNumericColumn = (values: ReadonlyArray<string>): boolean => {
  const nonEmptyValues = values.filter((v) => v !== "");
  return (
    nonEmptyValues.length > 0 &&
    nonEmptyValues.every((v) => !Number.isNaN(Number(v)))
  );
};

// Pure: compare two cell values per the column kind + direction.
export const compareCellValues = (
  av: string,
  bv: string,
  isNumeric: boolean,
  dir: SortDir,
): number => {
  const cmp = isNumeric
    ? Number(av) - Number(bv)
    : av.localeCompare(bv, undefined, { sensitivity: "base" });
  return dir === "asc" ? cmp : -cmp;
};

// Pure: produce a new array of rows in sorted order. Does NOT mutate the
// input array and does NOT touch the DOM — callers append in order. Exported
// for property testing (length-preservation, monotonicity, idempotence).
export const sortRowsPure = (
  rows: ReadonlyArray<HTMLTableRowElement>,
  key: string,
  dir: SortDir,
): HTMLTableRowElement[] => {
  const values = rows.map((r) => getCellValue(r, key));
  const numeric = isNumericColumn(values);
  return [...rows].sort((a, b) =>
    compareCellValues(getCellValue(a, key), getCellValue(b, key), numeric, dir),
  );
};

const sortRows = (
  tbody: HTMLTableSectionElement,
  key: string,
  dir: SortDir,
): void => {
  const rows = Array.from(tbody.querySelectorAll<HTMLTableRowElement>("tr"));
  const sorted = sortRowsPure(rows, key, dir);
  sorted.forEach((r) => tbody.appendChild(r));
};

// Pure: compute the next sort direction given the currently-active sort key
// and the column just clicked. Clicking the active asc column flips to desc;
// any other transition resets to asc. Exported for property testing.
export const nextSortDir = (
  currentKey: string | undefined,
  currentDir: SortDir,
  clickedKey: string,
): SortDir =>
  currentKey === clickedKey && currentDir === "asc" ? "desc" : "asc";

// Pure: read the current sort direction off the table, defaulting to "asc"
// and rejecting anything that isn't a valid SortDir tag.
export const readCurrentDir = (raw: string | undefined): SortDir => {
  if (raw === undefined) return "asc";
  try {
    return validSortDir(raw);
  } catch {
    return "asc";
  }
};

export const ClientTableSort = {
  mounted(this: LiveViewHook<never> & ClientTableSortState) {
    const table = this.el as HTMLTableElement;

    const onClick = (e: Event): void => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>(
        "[data-sort-col]",
      );
      if (!btn) return;
      const key = btn.dataset["sortCol"];
      if (!key) return;

      const currentKey = table.dataset["sortKey"];
      const currentDir = readCurrentDir(table.dataset["sortDir"]);
      const newDir: SortDir = nextSortDir(currentKey, currentDir, key);

      table.dataset["sortKey"] = key;
      table.dataset["sortDir"] = newDir;

      // Update header visual state
      table.querySelectorAll<HTMLElement>("[data-sort-col]").forEach((h) => {
        h.removeAttribute("data-sort-active");
        h.removeAttribute("data-sort-dir");
      });
      btn.dataset["sortActive"] = "";
      btn.dataset["sortDir"] = newDir;

      const tbody = table.querySelector("tbody");
      if (tbody) sortRows(tbody, key, newDir);
    };

    table.addEventListener("click", onClick);
    this._onClick = onClick;
  },

  destroyed(this: LiveViewHook<never> & ClientTableSortState) {
    this.el.removeEventListener("click", this._onClick as EventListener);
  },
};
